import { useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/lib/apiClient';
import { orgunitKeys } from '@/hooks/core/useOrgUnit';
import { authService } from '@/services/auth/authService';

/**
 * Renumber one sibling group of the organization tree.
 *
 * One request, one unit of work: the server assigns the sortOrder values (see
 * `OrgUnitAppService.ReorderAsync`), so a failure can't leave the group half-renumbered and a
 * reorder costs one audit record, not one per sibling.
 *
 * `onSuccess` RETURNS the invalidation promise, which React Query awaits before the mutation
 * leaves the pending state. That is what makes `isPending` cover the refetch too — without it,
 * a second Move click landing between the POST resolving and the fresh list arriving would
 * recompute the swap from the pre-reorder cache and silently do nothing.
 */
export function useReorderOrgUnits() {
  const queryClient = useQueryClient();
  const tenantId = authService.getTenantId();

  return useMutation<void, Error, string[]>({
    mutationFn: async (orderedOrgUnitIds: string[]) => {
      await apiClient.post('/api/core/orgunit/reorder', { orderedOrgUnitIds });
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: orgunitKeys.all(tenantId) }),
  });
}
